import { Fragment, useState, useEffect, useCallback } from 'react';
import { Trash2, Plus, Search, RefreshCw, Tag, AlertTriangle } from 'lucide-react';
import { fetchBin, searchItems, deleteAssignment, createAssignment, type BinDetail, type OtherBinLocation, type SearchResult } from '../../lib/layout-api';
import { useWorkspace } from '../../lib/workspace';
import { OtherBinsCell } from './OtherBinsCell';

interface BinItemListProps {
  binCode: string;
}

type RowStatus = 'assigned-ok' | 'assigned-missing' | 'unassigned-present';
type EslStatus = 'bound' | 'unbound' | 'double' | null;
interface UnifiedRow {
  item_id: string; description: string; assigned: boolean; assignment_id: number | null;
  in_stock: boolean; display_qty: string;
  unit_price: number; status: RowStatus;
  otherBins: OtherBinLocation[];
  itemKey: string | null;
  eslStatus: EslStatus;
  eslSerials: string[];
}

function formatQty(totalBase: number, qtyPerUom: number, salesUom: string, baseUom: string): string {
  if (qtyPerUom <= 1) return `${Math.round(totalBase * 100) / 100} ${baseUom}`;
  const salesQty = Math.floor(totalBase / qtyPerUom);
  const remainder = Math.round((totalBase % qtyPerUom) * 100) / 100;
  if (remainder > 0) return `${salesQty} ${salesUom}, ${remainder} ${baseUom}`;
  return `${salesQty} ${salesUom}`;
}

const STATUS_CFG: Record<RowStatus, { bg: string; border: string; label: string; badgeCls: string }> = {
  'assigned-ok':        { bg: 'bg-green-50',  border: 'border-l-2 border-l-status-green',  label: 'Stokta',    badgeCls: 'bg-green-100 text-green-700' },
  'assigned-missing':   { bg: 'bg-red-50',    border: 'border-l-2 border-l-status-red',    label: 'EKSIK',     badgeCls: 'bg-red-100 text-red-700' },
  'unassigned-present': { bg: 'bg-orange-50', border: 'border-l-2 border-l-status-orange', label: 'Atanmamis', badgeCls: 'bg-orange-100 text-orange-700' },
};

function buildRows(detail: BinDetail): UnifiedRow[] {
  const rows: UnifiedRow[] = [];
  const assignedItemIds = new Set(detail.assignments.map(a => a.item_id));
  const invByItem = new Map<string, typeof detail.inventory>();
  for (const inv of detail.inventory) {
    if (!invByItem.has(inv.item_id)) invByItem.set(inv.item_id, []);
    invByItem.get(inv.item_id)!.push(inv);
  }
  const otherMap = new Map<string, OtherBinLocation[]>();
  for (const ob of (detail.otherBins || [])) {
    if (!otherMap.has(ob.item_id)) otherMap.set(ob.item_id, []);
    otherMap.get(ob.item_id)!.push(ob);
  }

  for (const a of detail.assignments) {
    const invRows = invByItem.get(a.item_id) || [];
    const hasStock = invRows.some(r => r.qty_base > 0);
    const totalBase = invRows.reduce((s, r) => s + r.qty_base, 0);
    const ref = invRows[0];
    const displays = a.eslDisplays || [];
    let eslStatus: EslStatus = null;
    if (a.itemKey) {
      eslStatus = displays.length === 0 ? 'unbound' : displays.length > 1 ? 'double' : 'bound';
    }
    rows.push({
      item_id: a.item_id, description: a.description || ref?.description || '',
      assigned: true, assignment_id: a.id, in_stock: hasStock,
      display_qty: hasStock ? formatQty(totalBase, ref?.qty_per_uom || 1, ref?.sales_uom || '', ref?.base_uom || '') : '—',
      unit_price: ref?.unit_price || 0,
      status: hasStock ? 'assigned-ok' : 'assigned-missing',
      otherBins: otherMap.get(a.item_id) || [],
      itemKey: a.itemKey || null,
      eslStatus,
      eslSerials: displays.map(d => d.serial),
    });
  }
  for (const [itemId, invRows] of invByItem) {
    if (assignedItemIds.has(itemId)) continue;
    const totalBase = invRows.reduce((s, r) => s + r.qty_base, 0);
    const ref = invRows[0];
    rows.push({
      item_id: itemId, description: ref?.description || '',
      assigned: false, assignment_id: null, in_stock: true,
      display_qty: formatQty(totalBase, ref?.qty_per_uom || 1, ref?.sales_uom || '', ref?.base_uom || ''),
      unit_price: ref?.unit_price || 0,
      status: 'unassigned-present',
      otherBins: otherMap.get(itemId) || [],
      itemKey: null, eslStatus: null, eslSerials: [],
    });
  }
  return rows;
}

export function BinItemList({ binCode }: BinItemListProps) {
  const { openTab, replaceActiveTab, bottomPanel, setActiveTab } = useWorkspace();
  const [detail, setDetail] = useState<BinDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchRes, setSearchRes] = useState<SearchResult[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setDetail(await fetchBin(binCode)); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [binCode]);

  useEffect(() => { load(); }, [load]);

  async function handleRemove(id: number) {
    await deleteAssignment(id); load();
    window.dispatchEvent(new CustomEvent('bin-data-changed'));
  }
  async function handleAssign(itemId: string) {
    await createAssignment(binCode, itemId); load();
    setShowSearch(false); setSearchQuery(''); setSearchRes([]);
    window.dispatchEvent(new CustomEvent('bin-data-changed'));
  }
  async function doSearch(q: string) {
    setSearchQuery(q);
    if (q.length < 2) { setSearchRes([]); return; }
    try { setSearchRes(await searchItems(q)); } catch { setSearchRes([]); }
  }
  function openBinTab(code: string, e?: React.MouseEvent) {
    const tab = { type: 'bin' as const, label: code, params: { binCode: code }, closable: true };
    if (e?.ctrlKey || e?.metaKey) {
      openTab('bottom', { ...tab, id: `bin-${code}-${Date.now()}` });
    } else {
      const existing = bottomPanel.tabs.find(t => t.type === 'bin' && t.params.binCode === code);
      if (existing) {
        setActiveTab('bottom', existing.id);
      } else {
        replaceActiveTab('bottom', tab);
      }
    }
  }

  if (loading) return <div className="flex items-center justify-center py-8 text-tertiary text-[13px]">Yukleniyor...</div>;
  if (!detail) return <div className="flex items-center justify-center py-8 text-status-red text-[13px]">Veri yuklenemedi</div>;

  const rows = buildRows(detail);

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b shrink-0" style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
        <span className="text-[13px] font-semibold font-mono text-primary">{detail.code}</span>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
          detail.zone_code === 'CASHCARRY' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
        }`}>{detail.zone_code}</span>
        <span className="text-[11px] text-tertiary">{rows.length} urun</span>
        <div className="flex-1" />
        <button onClick={() => load()} className="p-1 text-tertiary hover:text-primary rounded-sm hover:bg-hover" title="Yenile">
          <RefreshCw size={13} />
        </button>
        {detail.zone_code === 'CASHCARRY' && (
          <button onClick={() => setShowSearch(!showSearch)}
            className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium bg-accent text-white rounded-sm hover:bg-accent/90">
            <Plus size={11} /> Urun Ata
          </button>
        )}
      </div>

      {/* Search */}
      {showSearch && (
        <div className="px-3 py-2 border-b bg-accent-light/30 shrink-0" style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
          <div className="flex items-center gap-2">
            <Search size={13} className="text-tertiary shrink-0" />
            <input type="text" value={searchQuery} onChange={e => doSearch(e.target.value)}
              placeholder="Urun ID veya isim ara..." autoFocus
              className="flex-1 px-2 py-1 text-[12px] border rounded-sm bg-white focus:outline-none focus:border-accent"
              style={{ borderColor: 'rgba(33,35,39,0.15)' }} />
            <button onClick={() => { setShowSearch(false); setSearchQuery(''); setSearchRes([]); }}
              className="text-[11px] text-tertiary hover:text-primary">Kapat</button>
          </div>
          {searchRes.length > 0 && (
            <div className="mt-1.5 max-h-28 overflow-auto border rounded-sm bg-white" style={{ borderColor: 'rgba(33,35,39,0.1)' }}>
              {searchRes.map(item => (
                <div key={item.item_id}
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-hover cursor-pointer border-b last:border-0 text-[12px]"
                  style={{ borderColor: 'rgba(33,35,39,0.05)' }}
                  onClick={() => handleAssign(item.item_id)}>
                  <span className="font-mono text-accent w-14 shrink-0">{item.item_id}</span>
                  <span className="flex-1 text-primary truncate">{item.description}</span>
                  <span className="text-tertiary text-[10px]">{item.brand}</span>
                  <span className="text-accent text-[10px] font-medium">Ata</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto min-h-0">
        {rows.length > 0 || (detail.orphanEsl ?? []).length > 0 ? (
          <>
          <table className="w-full max-w-5xl">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b" style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
                <th className="w-1" />
                <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-tertiary">Urun No</th>
                <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-tertiary">Aciklama</th>
                <th className="px-3 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-tertiary">Miktar</th>
                <th className="px-3 py-1.5 text-center text-[10px] font-semibold uppercase tracking-wider text-tertiary">ESL</th>
                <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-tertiary">Diger Raflar</th>
                <th className="px-3 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-tertiary">Durum</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const cfg = STATUS_CFG[row.status];
                const diagnostics: string[] = [];
                const assignedOthers = row.otherBins.filter(o => o.assigned).map(o => o.bin_code);
                if (assignedOthers.length > 0) diagnostics.push(`Bu urun ayrica atanmis: ${assignedOthers.join(', ')}`);
                if (row.status === 'unassigned-present') diagnostics.push('Bu rafa atanmamis ama envanteri var');
                if (row.eslStatus === 'unbound') diagnostics.push('Bu koda ESL etiketi atanmamis');
                if (row.eslStatus === 'double') diagnostics.push(`${row.eslSerials.length} ESL bagli: ${row.eslSerials.join(', ')}`);
                return (
                  <Fragment key={`${row.item_id}-${row.status}`}>
                  <tr className={`${cfg.bg} border-b hover:brightness-95 transition-all`}
                    style={{ borderColor: diagnostics.length > 0 ? 'transparent' : 'rgba(33,35,39,0.05)' }}>
                    <td className={cfg.border} />
                    <td className="px-3 py-1.5 font-mono text-[12px] text-accent">{row.item_id}</td>
                    <td className="px-3 py-1.5 text-[12px] text-primary">{row.description}</td>
                    <td className="px-3 py-1.5 text-[12px] text-right font-semibold text-primary whitespace-nowrap">
                      {row.display_qty}
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      {row.eslStatus === 'bound' && (
                        <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-mono font-semibold bg-blue-100 text-blue-700">
                          <Tag size={9} /> {row.eslSerials[0]}
                        </span>
                      )}
                      {row.eslStatus === 'unbound' && (
                        <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-semibold bg-red-100 text-red-700" title="Etiket yok">
                          <Tag size={9} /> Yok
                        </span>
                      )}
                      {row.eslStatus === 'double' && (
                        <span className="inline-flex flex-wrap items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-mono font-semibold bg-orange-100 text-orange-700">
                          <Tag size={9} /> {row.eslSerials.join(' + ')}
                        </span>
                      )}
                      {row.eslStatus === null && (
                        <span className="text-[9px] text-tertiary">—</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      <OtherBinsCell bins={row.otherBins} onOpenBin={openBinTab} />
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${cfg.badgeCls}`}>{cfg.label}</span>
                    </td>
                    <td className="px-1.5 py-1.5">
                      {detail.zone_code === 'CASHCARRY' && row.assigned ? (
                        <button onClick={() => row.assignment_id && handleRemove(row.assignment_id)}
                          title="Atamayi kaldir" className="rounded p-1 text-tertiary hover:text-status-red hover:bg-red-50 transition-colors">
                          <Trash2 size={12} />
                        </button>
                      ) : detail.zone_code === 'CASHCARRY' && !row.assigned ? (
                        <button onClick={() => handleAssign(row.item_id)}
                          title="Bu urunu ata" className="rounded p-1 text-tertiary hover:text-accent hover:bg-accent-light transition-colors">
                          <Plus size={12} />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                  {diagnostics.length > 0 && (
                    <tr className={`${cfg.bg} border-b`} style={{ borderColor: 'rgba(33,35,39,0.05)' }}>
                      <td className={cfg.border} />
                      <td colSpan={7} className="px-3 pb-1.5 -mt-1">
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-secondary">
                          {diagnostics.map((d, i) => (
                            <span key={i} className="inline-flex items-center gap-1">
                              <AlertTriangle size={10} className="text-status-orange" /> {d}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {/* Orphan ESL bindings */}
          {(detail.orphanEsl ?? []).length > 0 && (
            <div className="mx-3 mt-2 mb-1">
              <div className="flex items-center gap-1.5 mb-1.5">
                <AlertTriangle size={12} className="text-orange-500" />
                <span className="text-[11px] font-semibold text-orange-700">Sahipsiz Etiketler</span>
                <span className="text-[10px] text-tertiary">({detail.orphanEsl!.length})</span>
              </div>
              {detail.orphanEsl!.map(o => (
                <div key={o.key} className="flex items-center gap-2 px-3 py-2 mb-1 rounded border border-orange-200 bg-orange-50">
                  <Tag size={11} className="text-orange-500 shrink-0" />
                  <span className="text-[11px] font-mono text-orange-700">{o.key}</span>
                  <span className="text-[10px] text-tertiary">Urun yok</span>
                  <div className="flex-1" />
                  {o.displays.map(d => (
                    <span key={d.serial} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-blue-100 text-blue-700">
                      <Tag size={8} /> {d.serial}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          )}
          </>
        ) : (
          <div className="text-tertiary text-center py-8 text-[13px]">Bu raf bos.</div>
        )}
      </div>
    </div>
  );
}
